import { useEventCallback } from "@stenajs-webui/core";
import { useEffect, useMemo } from "react";

import { useAppNavigate } from "../../../../../common/hooks/useAppNavigate";
import { useHasPermission } from "../../../../../common/hooks/useHasPermission";
import { modalRouteCreators } from "../../../../../common/routes/AppRoutes";
import { BookingFormInstanceIdProvider } from "../../../../../common/update-information/BookingFormInstanceIdProvider";
import { Permission } from "../../../../../services/authentication/utils/authUtils";
import { initializeUpdateFormWithBooking } from "../../../../../services/update-form/updateFormActionsHelpers";
import type { FormInstanceId } from "../../../../../services/update-form/updateFormReducer";
import { isBookingEditable } from "../../../../../services/update-form/utils/bookingUtils";
import { useAppDispatch } from "../../../../../store/appDispatch";
import {
  BookingDetailsEditContainer,
  type BookingDetailsEditContainerProps,
} from "./BookingDetailsEditContainer";

interface BookingDetailsEditProps extends BookingDetailsEditContainerProps {}

export const BookingDetailsEdit = ({
  booking,
  onRequestClose,
}: BookingDetailsEditProps) => {
  const dispatch = useAppDispatch();
  const getHasPermission = useHasPermission();
  const { replace } = useAppNavigate();
  const { bookingNo } = booking;

  const isEditable =
    getHasPermission(Permission.EditBookings) && isBookingEditable(booking);

  const initialize = useEventCallback(() => {
    if (isEditable) {
      dispatch(initializeUpdateFormWithBooking(booking, "Details"));
    }
  });

  useEffect(() => {
    if (!isEditable) {
      replace(modalRouteCreators.bookingDetailsView({ bookingNo }));
    }
  }, [booking, bookingNo, getHasPermission, isEditable, replace]);

  useEffect(() => {
    initialize();
  }, [bookingNo, initialize]);

  const formInstanceId = useMemo<FormInstanceId>(
    () => ({
      bookingNo,
      formId: "Details",
    }),
    [bookingNo],
  );

  if (!isEditable) {
    return null;
  }

  return (
    <BookingFormInstanceIdProvider value={formInstanceId} key={bookingNo}>
      <BookingDetailsEditContainer
        onRequestClose={onRequestClose}
        booking={booking}
      />
    </BookingFormInstanceIdProvider>
  );
};
